import { useEffect } from 'react';

/**
 * Track banner display based on scroll position
 * @param topRef - Ref to the top tracking element (start point for visibility).
 * @param bottomRef - Ref to the bottom tracking element (end point for visibility).
 * @param setIsVisible - Callback to update the visibility state of the banner.
 * @param threshold - Scroll threshold (in pixels) from the top of the viewport to trigger visibility.
 */
export function useScrollVisibility(
  topRef: React.RefObject<HTMLElement>,
  bottomRef: React.RefObject<HTMLElement>,
  setIsVisible: (visible: boolean) => void,
  threshold: number
) {
  useEffect(() => {
    if (!topRef.current || !bottomRef.current) return () => {};

    const topElement = topRef.current;
    const bottomElement = bottomRef.current;

    const handleScroll = () => {
      const topOffset = topElement.getBoundingClientRect().top;
      const bottomOffset = bottomElement.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const documentHeight = document.body.scrollHeight;

      // Consider as "scrolled to bottom" if within 10px of the page bottom
      const isScrolledToBottom =
        scrollTop + windowHeight >= documentHeight - 10;

      if (isScrolledToBottom || bottomOffset < threshold) {
        setIsVisible(false);
      } else if (topOffset < 0) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    const onScroll = () => requestAnimationFrame(handleScroll);

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [topRef, bottomRef, setIsVisible, threshold]);
}
